import React, { useState, useMemo } from 'react';
import {
  FileText, Search, Filter, Download, Calendar, User, AlertCircle,
  CheckCircle, Trash2, Mail, Clock, Database, Shield, ExternalLink, ChevronDown, ChevronUp
} from 'lucide-react';
import type { AuditActionType, Jurisdiction } from '../../types/dataRetention';
import { mockAuditLogs } from '../../data/mockAuditLogs';
import { StandardButton, StandardInput, StandardSelect, StandardEmptyState } from '../standard';

interface AuditLogViewerProps {
  userRole: 'system_admin' | 'compliance_officer' | 'viewer';
}

const AuditLogViewer: React.FC<AuditLogViewerProps> = ({ userRole }) => {
  const [selectedActionTypes, setSelectedActionTypes] = useState<AuditActionType[]>([]);
  const [selectedJurisdiction, setSelectedJurisdiction] = useState<Jurisdiction | 'SYSTEM' | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [expandedLogId, setExpandedLogId] = useState<string | number | null>(null);
  const [sortBy, setSortBy] = useState<'timestamp' | 'actionType' | 'jurisdiction'>('timestamp');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Toggle action type selection (multi-select)
  const toggleActionType = (actionType: AuditActionType) => {
    setSelectedActionTypes(prev =>
      prev.includes(actionType)
        ? prev.filter(t => t !== actionType)
        : [...prev, actionType]
    );
  };

  // Filter and sort logs
  const filteredAndSortedLogs = useMemo(() => {
    let filtered = mockAuditLogs.filter(log => {
      // Action type filter (multi-select)
      if (selectedActionTypes.length > 0 && !selectedActionTypes.includes(log.actionType)) return false;

      // Jurisdiction filter
      if (selectedJurisdiction !== 'all' && log.jurisdiction !== selectedJurisdiction) return false;

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          log.userId?.toLowerCase().includes(query) ||
          log.userName?.toLowerCase().includes(query) ||
          log.actionType.toLowerCase().includes(query) ||
          log.performedBy?.toLowerCase().includes(query) ||
          log.performedByName?.toLowerCase().includes(query) ||
          log.details?.toLowerCase().includes(query)
        );
      }

      // Date range filter
      if (dateRange.start && new Date(log.performedAt) < new Date(dateRange.start)) return false;
      if (dateRange.end && new Date(log.performedAt) > new Date(dateRange.end)) return false;

      return true;
    });

    // Sort logs
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'timestamp':
          comparison = a.performedAt.getTime() - b.performedAt.getTime();
          break;
        case 'actionType':
          comparison = a.actionType.localeCompare(b.actionType);
          break;
        case 'jurisdiction':
          comparison = a.jurisdiction.localeCompare(b.jurisdiction);
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [mockAuditLogs, selectedActionTypes, selectedJurisdiction, searchQuery, dateRange, sortBy, sortOrder]);

  // Action type colors and icons
  const getActionTypeIcon = (actionType: AuditActionType) => {
    switch (actionType) {
      case 'status_change': return <CheckCircle className="w-4 h-4" />;
      case 'manual_intervention': return <User className="w-4 h-4" />;
      case 'data_export': return <Download className="w-4 h-4" />;
      case 'deletion_executed': return <Trash2 className="w-4 h-4" />;
      case 'extension_granted': return <Clock className="w-4 h-4" />;
      case 'legal_hold_applied': return <Shield className="w-4 h-4" />;
      case 'legal_hold_removed': return <Shield className="w-4 h-4" />;
      case 'policy_change': return <FileText className="w-4 h-4" />;
      case 'warning_sent': return <Mail className="w-4 h-4" />;
      case 'export_generated': return <Download className="w-4 h-4" />;
      case 'purge_executed': return <Trash2 className="w-4 h-4" />;
      case 'deletion_request': return <FileText className="w-4 h-4" />;
      case 'retention_extended': return <Clock className="w-4 h-4" />;
      case 'jurisdiction_updated': return <Database className="w-4 h-4" />;
      case 'inactivity_scan': return <Search className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getActionTypeColor = (actionType: AuditActionType) => {
    switch (actionType) {
      case 'status_change': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'manual_intervention': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'data_export': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'deletion_executed': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'extension_granted': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'legal_hold_applied': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'legal_hold_removed': return 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300';
      case 'policy_change': return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300';
      case 'warning_sent': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'export_generated': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'purge_executed': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'deletion_request': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'retention_extended': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'jurisdiction_updated': return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300';
      case 'inactivity_scan': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getActionTypeDisplay = (actionType: AuditActionType): string => {
    return actionType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const getJurisdictionBadge = (jurisdiction: Jurisdiction | 'SYSTEM') => {
    const colors: Record<Jurisdiction | 'SYSTEM', string> = {
      US: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      CA: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      QC: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      UK: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      SYSTEM: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
    };
    return colors[jurisdiction] || 'bg-gray-100 text-gray-800';
  };

  const exportAuditLogs = (format: 'csv' | 'json') => {
    const timestamp = new Date().toISOString().split('T')[0];

    if (format === 'csv') {
      const csv = [
        ['ID', 'Timestamp', 'User ID', 'User Name', 'Action Type', 'Performed By', 'Jurisdiction', 'Details', 'IP Address', 'Reason'].join(','),
        ...filteredAndSortedLogs.map(log => [
          log.id,
          log.performedAt.toISOString(),
          log.userId || 'SYSTEM',
          log.userName || '',
          log.actionType,
          log.performedByName || log.performedBy || 'SYSTEM',
          log.jurisdiction,
          `"${log.details || ''}"`,
          log.metadata.ipAddress || '',
          `"${log.metadata.reason || ''}"`
        ].join(','))
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${timestamp}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } else {
      const json = JSON.stringify(filteredAndSortedLogs, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${timestamp}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Data Retention Audit Log
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Showing {filteredAndSortedLogs.length} of {mockAuditLogs.length} audit log entries
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StandardButton
            variant="success"
            onClick={() => exportAuditLogs('csv')}
            leftIcon={<Download className="w-4 h-4" />}
          >
            Export CSV
          </StandardButton>
          <StandardButton
            variant="primary"
            onClick={() => exportAuditLogs('json')}
            leftIcon={<Download className="w-4 h-4" />}
          >
            Export JSON
          </StandardButton>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Logs</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {mockAuditLogs.length}
              </p>
            </div>
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Deletion Actions</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                {mockAuditLogs.filter(l => l.actionType === 'deletion_executed' || l.actionType === 'purge_executed').length}
              </p>
            </div>
            <Trash2 className="w-8 h-8 text-red-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Data Exports</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {mockAuditLogs.filter(l => l.actionType === 'data_export' || l.actionType === 'export_generated').length}
              </p>
            </div>
            <Download className="w-8 h-8 text-green-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Extensions Granted</p>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {mockAuditLogs.filter(l => l.actionType === 'extension_granted' || l.actionType === 'retention_extended').length}
              </p>
            </div>
            <Clock className="w-8 h-8 text-purple-400" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Action Type filter (multi-select dropdown) */}
          <div className="relative">
            <button
              type="button"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 text-left flex items-center justify-between"
              onClick={() => {
                const dropdown = document.getElementById('action-type-dropdown');
                dropdown?.classList.toggle('hidden');
              }}
            >
              <span>
                {selectedActionTypes.length === 0
                  ? 'All Actions'
                  : `${selectedActionTypes.length} selected`}
              </span>
              <Filter className="w-4 h-4" />
            </button>
            <div
              id="action-type-dropdown"
              className="hidden absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-auto"
            >
              {(['status_change', 'manual_intervention', 'data_export', 'deletion_executed', 'extension_granted', 'legal_hold_applied', 'legal_hold_removed', 'policy_change'] as AuditActionType[]).map((type) => (
                <label key={type} className="flex items-center px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedActionTypes.includes(type)}
                    onChange={() => toggleActionType(type)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-900 dark:text-white">{getActionTypeDisplay(type)}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Jurisdiction filter */}
          <div>
            <StandardSelect
              value={selectedJurisdiction}
              onChange={(e) => setSelectedJurisdiction(e.target.value as any)}
              options={[
                { value: 'all', label: 'All Jurisdictions' },
                { value: 'US', label: 'United States (CCPA)' },
                { value: 'CA', label: 'Canada (PIPEDA)' },
                { value: 'QC', label: 'Quebec (Law 25)' },
                { value: 'UK', label: 'United Kingdom (GDPR)' },
                { value: 'SYSTEM', label: 'System' }
              ]}
            />
          </div>

          {/* Date Range */}
          <div className="flex gap-2">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Audit Logs Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => {
                    if (sortBy === 'timestamp') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortBy('timestamp');
                      setSortOrder('desc');
                    }
                  }}
                >
                  <div className="flex items-center gap-1">
                    Timestamp
                    {sortBy === 'timestamp' && (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => {
                    if (sortBy === 'actionType') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortBy('actionType');
                      setSortOrder('asc');
                    }
                  }}
                >
                  <div className="flex items-center gap-1">
                    Action Type
                    {sortBy === 'actionType' && (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  User Affected
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Performed By
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => {
                    if (sortBy === 'jurisdiction') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortBy('jurisdiction');
                      setSortOrder('asc');
                    }
                  }}
                >
                  <div className="flex items-center gap-1">
                    Jurisdiction
                    {sortBy === 'jurisdiction' && (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredAndSortedLogs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer" onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 dark:text-white">{log.performedAt.toLocaleDateString()}</span>
                        <span className="text-xs">{log.performedAt.toLocaleTimeString()}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex items-center gap-1 text-xs leading-5 font-semibold rounded-full ${getActionTypeColor(log.actionType)}`}>
                        {getActionTypeIcon(log.actionType)}
                        {getActionTypeDisplay(log.actionType)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 dark:text-white">{log.userName || 'N/A'}</span>
                        {log.userId && <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">ID: {log.userId}</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {log.performedByName || log.performedBy || 'SYSTEM'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getJurisdictionBadge(log.jurisdiction)}`}>
                        {log.jurisdiction}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                      <div className="flex items-center justify-between gap-2">
                        <span className="line-clamp-1">{log.details}</span>
                        {expandedLogId === log.id ? <ChevronUp className="w-4 h-4 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 flex-shrink-0" />}
                      </div>
                    </td>
                  </tr>
                  {expandedLogId === log.id && (
                    <tr className="bg-gray-50 dark:bg-gray-700/30">
                      <td colSpan={6} className="px-6 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-3">
                            <h4 className="font-semibold text-sm text-gray-900 dark:text-white">Audit Details</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex gap-2">
                                <span className="text-gray-600 dark:text-gray-400 font-medium">Full Timestamp:</span>
                                <span className="text-gray-900 dark:text-white">{log.performedAt.toISOString()}</span>
                              </div>
                              {log.metadata.ipAddress && (
                                <div className="flex gap-2">
                                  <span className="text-gray-600 dark:text-gray-400 font-medium">IP Address:</span>
                                  <span className="text-gray-900 dark:text-white font-mono">{log.metadata.ipAddress}</span>
                                </div>
                              )}
                              {log.metadata.userAgent && (
                                <div className="flex gap-2">
                                  <span className="text-gray-600 dark:text-gray-400 font-medium">User Agent:</span>
                                  <span className="text-gray-900 dark:text-white text-xs truncate">{log.metadata.userAgent}</span>
                                </div>
                              )}
                              {log.metadata.reason && (
                                <div className="flex gap-2">
                                  <span className="text-gray-600 dark:text-gray-400 font-medium">Reason:</span>
                                  <span className="text-gray-900 dark:text-white">{log.metadata.reason}</span>
                                </div>
                              )}
                              {log.metadata.notes && (
                                <div className="flex gap-2">
                                  <span className="text-gray-600 dark:text-gray-400 font-medium">Notes:</span>
                                  <span className="text-gray-900 dark:text-white">{log.metadata.notes}</span>
                                </div>
                              )}
                              {log.metadata.relatedRequestId && (
                                <div className="flex gap-2">
                                  <span className="text-gray-600 dark:text-gray-400 font-medium">Related Request:</span>
                                  <span className="text-gray-900 dark:text-white font-mono">{log.metadata.relatedRequestId}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="space-y-3">
                            <h4 className="font-semibold text-sm text-gray-900 dark:text-white">Changes</h4>
                            {(log.metadata.beforeValue || log.metadata.afterValue) && (
                              <div className="space-y-2 text-sm">
                                {log.metadata.beforeValue && (
                                  <div>
                                    <span className="text-gray-600 dark:text-gray-400 font-medium">Before:</span>
                                    <pre className="mt-1 text-xs bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-600 overflow-x-auto">
                                      {JSON.stringify(log.metadata.beforeValue, null, 2)}
                                    </pre>
                                  </div>
                                )}
                                {log.metadata.afterValue && (
                                  <div>
                                    <span className="text-gray-600 dark:text-gray-400 font-medium">After:</span>
                                    <pre className="mt-1 text-xs bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-600 overflow-x-auto">
                                      {JSON.stringify(log.metadata.afterValue, null, 2)}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            )}
                            {log.exportFilePath && (
                              <div className="flex items-center gap-2">
                                <Download className="w-4 h-4 text-gray-500" />
                                <span className="text-gray-700 dark:text-gray-300 font-medium">Export File:</span>
                                <span className="font-mono text-blue-600 dark:text-blue-400 text-xs">{log.exportFilePath}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {filteredAndSortedLogs.length === 0 && (
          <div className="text-center py-12">
            <StandardEmptyState
              title="No audit logs found"
              message="No audit logs match your current filters"
            />
          </div>
        )}
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300">Immutable Audit Trail</h4>
            <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
              All audit logs are immutable and retained for 7 years for compliance purposes.
              Logs cannot be modified or deleted. Database triggers prevent any changes to this table.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogViewer;
